import * as React from 'react';

import { cn } from '../lib/utils';

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  borderClassName?: string;
  mode?: 'light' | 'dark';
};

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, mode = 'light', ...props }, ref) => (
    <table
      ref={ref}
      data-mode={mode}
      className={cn('vai-w-full vai-caption-bottom vai-text-sm', className)}
      {...props}
    />
  ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      '[&_tr]:vai-border-b dark:[&_tr]:vai-border-zinc-700',
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:vai-border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'vai-border-t vai-bg-muted/50 vai-font-medium [&>tr]:last:vai-border-b-0 dark:vai-border-zinc-700 dark:hover:vai-bg-zinc-900 dark:vai-bg-transparent',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    data-testid="table-row"
    className={cn(
      'vai-border-b vai-transition-colors hover:vai-bg-muted/50 data-[state=selected]:vai-bg-muted dark:vai-border-zinc-700 dark:hover:vai-bg-zinc-900',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'vai-h-12 vai-px-4 vai-text-left vai-align-middle vai-font-medium vai-text-muted-foreground [&:has([role=checkbox])]:vai-pr-0',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    dataTestId?: string;
  }
>(({ className, dataTestId, ...props }, ref) => (
  <td
    ref={ref}
    data-testid={dataTestId}
    className={cn(
      'vai-font-inter vai-p-4 vai-align-middle [&:has([role=checkbox])]:vai-pr-0',
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('vai-mt-4 vai-text-sm vai-text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
